// utils/order/helpers.js
import { useTranslation } from "@/hooks/useTranslation";

/**
 * Format ngày tháng theo ngôn ngữ hiện tại
 * @param {string} dateString - Chuỗi ngày tháng dạng ISO
 * @param {boolean} includeTime - Có hiển thị giờ phút hay không
 * @returns {string} Ngày tháng đã được định dạng
 */
export function formatDateTime(dateString, includeTime = true) {
    if (!dateString) return "N/A";

    try {
        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return "N/A";
        }

        // Lấy ngôn ngữ từ localStorage hoặc mặc định là 'vi'
        const language = localStorage.getItem("cmsLanguage") || "vi";
        const locale = language === "vi" ? "vi-VN" : "en-US";

        const options = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        };

        if (includeTime) {
            options.hour = "2-digit";
            options.minute = "2-digit";
        }

        return new Intl.DateTimeFormat(locale, options).format(date);
    } catch (error) {
        console.error("Error formatting date:", error);
        return "N/A";
    }
}

/**
 * Format số điện thoại theo định dạng 4-3-3 (XXXX XXX XXX)
 * @param {string} phoneNumber - Số điện thoại cần định dạng
 * @returns {string} Số điện thoại đã được định dạng
 */
export function formatPhoneNumber(phoneNumber) {
    if (!phoneNumber) return '';
    
    // Xóa tất cả ký tự không phải số
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Kiểm tra độ dài để đảm bảo đó là số điện thoại hợp lệ
    if (cleaned.length < 10) return phoneNumber;
    
    // Định dạng theo kiểu 4-3-3
    return `${cleaned.substring(0, 4)} ${cleaned.substring(4, 7)} ${cleaned.substring(7)}`;
};

/**
 * Format số tiền theo ngôn ngữ hiện tại
 * @param {number} amount - Số tiền cần định dạng
 * @param {string} currency - Đơn vị tiền tệ (mặc định: VND)
 * @returns {string} Số tiền đã được định dạng
 */
export function formatCurrency(amount, currency = "VND") {
    if (amount === undefined || amount === null || isNaN(parseFloat(amount))) {
        return "0 VND";
    }

    try {
        const language = localStorage.getItem("cmsLanguage") || "vi";
        const locale = language === "vi" ? "vi-VN" : "en-US";

        if (currency === "VND") {
            // VND không sử dụng phần thập phân
            return new Intl.NumberFormat(locale, {
                style: "currency",
                currency: "VND",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(amount);
        } else {
            return new Intl.NumberFormat(locale, {
                style: "currency",
                currency: currency,
            }).format(amount);
        }
    } catch (error) {
        console.error("Error formatting currency:", error);
        return `${amount} ${currency}`;
    }
}

/**
 * Rút gọn chuỗi văn bản dài
 * @param {string} text - Chuỗi văn bản cần rút gọn
 * @param {number} maxLength - Độ dài tối đa (mặc định: 50)
 * @returns {string} Chuỗi văn bản đã được rút gọn
 */
export function truncateText(text, maxLength = 50) {
    if (!text) return "";

    if (text.length <= maxLength) return text;

    return `${text.substring(0, maxLength)}...`;
}

/**
 * Tạo mã đơn hàng ngẫu nhiên
 * @returns {string} Mã đơn hàng dạng ORD + 9 số ngẫu nhiên
 */
export function generateOrderId() {
    return `ORD${Math.floor(Math.random() * 1000000000)
        .toString()
        .padStart(9, "0")}`;
}

/**
 * Tính tổng tiền đơn hàng từ danh sách sản phẩm
 * @param {Array} items - Danh sách sản phẩm
 * @returns {number} Tổng tiền đơn hàng
 */
export function calculateOrderTotal(items) {
    if (!items || !items.length) return 0;

    return items.reduce((total, item) => {
        const itemTotal = (item.quantity || 0) * (item.unit_price || 0);
        return total + itemTotal;
    }, 0);
}

/**
 * Xây dựng URL API từ endpoint và các tham số
 * @param {string} endpoint - Endpoint từ API_ENDPOINTS
 * @param {Object} params - Object chứa các tham số để thay thế trong endpoint
 * @param {URLSearchParams} queryParams - Tham số query string (tùy chọn)
 * @returns {string} URL API hoàn chỉnh
 */
export const buildApiUrl = (endpoint, params = {}, queryParams) => {
    let url = endpoint;
    
    // Thay thế các tham số trong URL
    Object.keys(params).forEach(key => {
        url = url.replace(`:${key}`, params[key]);
    });
    
    // Nối query string nếu có
    if (queryParams instanceof URLSearchParams && queryParams.toString()) {
        url = `${url}?${queryParams.toString()}`;
    }
    
    return url;
};
